import { IPCodeInput, IPCodeInputToken } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  canUseNameMode,
  expressionErrorMessage,
  ExpressionReference,
  ExpressionValidationState,
  getGrandfatheredReferenceIds,
  idToName,
  nameToId,
  tokenizeExpression,
} from 'components/KPI/Expression/expressionEditor.helpers';
import { useExpressionSuggestions, useExpressionValidation } from 'components/KPI/Expression/expressionEditor.hooks';
import ExpressionStatusStrip, { ExpressionStatus } from 'components/KPI/Expression/ExpressionStatusStrip';
import ExpressionSuggestionList from 'components/KPI/Expression/ExpressionSuggestionList';
import InlineSuggestions from 'components/Suggestions/InlineSuggestions';
import { globalStackTokensGapExtraSmall } from 'globalStyles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type { ExpressionValidationState };

interface IExpressionEditorProps {
  id: string;
  labelId: string;
  value: string;
  originalValue: string;
  onChange: (storedValue: string) => void;
  references?: ExpressionReference[];
  onValidationStateChange?: (state: ExpressionValidationState) => void;
  disabled?: boolean;
  maxLength?: number;
}

const ExpressionEditor = (props: IExpressionEditorProps) => {
  const { t } = useTranslation(['kpis']);
  const references = props.references ?? [];
  const poolProvided = props.references !== undefined;
  const referencesKey = references
    .map((reference) => `${reference.id}:${reference.name}:${reference.isNumeric}:${reference.isSelectable}`)
    .join('|');
  // One stable array identity per catalog content, so downstream hooks can use normal deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableReferences = useMemo(() => references, [referencesKey]);

  const selectableReferences = stableReferences.filter((reference) => reference.isSelectable);
  const hasSuggestions = selectableReferences.length > 0;

  const grandfatheredIds = useMemo(
    () => getGrandfatheredReferenceIds(props.originalValue, stableReferences),
    [props.originalValue, stableReferences],
  );

  const [display, setDisplay] = useState<{ nameMode: boolean; text: string }>(() => {
    const nameMode = canUseNameMode(props.value, stableReferences);

    return { nameMode, text: nameMode ? idToName(props.value, stableReferences) : props.value };
  });
  const { nameMode, text } = display;
  const toStored = (next: string): string => (nameMode ? nameToId(next, stableReferences) : next);

  const validation = useExpressionValidation({
    text,
    references: stableReferences,
    poolProvided,
    grandfatheredIds,
    onValidationStateChange: props.onValidationStateChange,
  });
  const { currentVerdict, error } = validation;

  const applyText = (next: string) => {
    setDisplay({ nameMode, text: next });
    validation.reset();
    props.onChange(toStored(next));
  };

  const suggestions = useExpressionSuggestions({
    text,
    nameMode,
    references: stableReferences,
    disabled: props.disabled,
    hasSuggestions,
    applyText,
  });
  const { suggestion, setSuggestion } = suggestions;

  const listId = `${props.id}-suggestions`;
  const statusId = `${props.id}-status`;

  // Latest-ref for the sync effect below: it must read the display current at effect time
  // without re-firing on every keystroke. Declared first so it commits before the sync effect.
  const displayRef = useRef(display);
  useEffect(() => {
    displayRef.current = display;
  });

  useEffect(() => {
    const stored = props.value;
    const { nameMode: currentNameMode, text: currentText } = displayRef.current;
    const currentStored = currentNameMode ? nameToId(currentText, stableReferences) : currentText;
    if (currentStored !== stored) {
      const nextNameMode = canUseNameMode(stored, stableReferences);
      setDisplay({ text: nextNameMode ? idToName(stored, stableReferences) : stored, nameMode: nextNameMode });
      setSuggestion(undefined);
    }
  }, [props.value, stableReferences, setSuggestion]);

  const getTokens = useMemo(() => {
    const pool = poolProvided ? stableReferences : undefined;

    return (input: string): IPCodeInputToken[] =>
      tokenizeExpression(input, pool).map((token): IPCodeInputToken => {
        if (token.type === 'field' && token.reference) {
          const validReference =
            token.reference.isNumeric && (token.reference.isSelectable || grandfatheredIds.has(token.reference.id));

          return { text: token.text, kind: validReference ? 'accent' : 'error' };
        }
        if (token.type === 'unknownField') return { text: token.text, kind: 'error' };

        return { text: token.text, kind: token.type === 'operator' ? 'muted' : 'plain' };
      });
  }, [stableReferences, grandfatheredIds, poolProvided]);

  const deriveStatus = (): ExpressionStatus => {
    if (text.trim() === '') return { kind: 'empty' };
    if (!currentVerdict) return { kind: 'validating' };
    if (!error) return { kind: 'valid', example: currentVerdict.example };

    const action =
      error.kind === 'unknownField' && error.suggestion
        ? {
            text: t('kpis:Expression.ApplyFix'),
            onClick: () => suggestions.applySuggestionFix(error.name, error.suggestion as string),
          }
        : undefined;

    return { kind: 'error', action };
  };

  const activeItem = suggestion?.items[suggestion.active];

  return (
    <IPStack>
      <IPStack tokens={globalStackTokensGapExtraSmall}>
        <IPStack.Item>
          <IPCodeInput
            id={props.id}
            componentRef={suggestions.inputRef}
            value={text}
            rows={3}
            maxLength={props.maxLength ?? 1024}
            disabled={props.disabled}
            errorMessage={error ? expressionErrorMessage(t, error) : undefined}
            getTokens={getTokens}
            onCaretPointChange={hasSuggestions ? suggestions.setCaretPoint : undefined}
            role={hasSuggestions ? 'combobox' : undefined}
            aria-labelledby={props.labelId}
            aria-describedby={statusId}
            aria-autocomplete={hasSuggestions ? 'list' : undefined}
            aria-expanded={hasSuggestions ? !!suggestion : undefined}
            aria-controls={suggestion ? listId : undefined}
            aria-activedescendant={activeItem ? `${listId}-${activeItem.id}` : undefined}
            onChange={suggestions.onFieldChange}
            onKeyDown={suggestions.onKeyDown}
            onKeyUp={suggestions.onKeyUp}
            onClick={suggestions.onClick}
            onBlur={suggestions.onBlur}
          />
        </IPStack.Item>
        <IPStack.Item>
          <ExpressionStatusStrip id={statusId} status={deriveStatus()} />
        </IPStack.Item>
        {hasSuggestions && !props.disabled && (
          <IPStack.Item>
            <InlineSuggestions
              items={selectableReferences.map((reference) => ({ id: reference.id, label: reference.name }))}
              onSelect={(item) => {
                const reference = selectableReferences.find((candidate) => candidate.id === item.id);
                if (reference) suggestions.insertAtCaret(reference);
              }}
              text={{
                show: t('kpis:Expression.InsertFieldShow'),
                label: t('kpis:Expression.InsertFieldLabel'),
                hint: t('kpis:Expression.InsertFieldHint'),
                dismiss: t('kpis:Expression.InsertFieldDismiss'),
              }}
            />
          </IPStack.Item>
        )}
      </IPStack>
      {suggestion && (
        <IPCallout
          target={suggestions.caretPoint ?? `#${props.id}`}
          isBeakVisible={false}
          gapSpace={2}
          directionalHint={IPDirectionalHint.bottomLeftEdge}
          setInitialFocus={false}
          onDismiss={() => setSuggestion(undefined)}
          calloutMaxHeight={240}
          styles={{ root: { minWidth: 300 } }}
        >
          <ExpressionSuggestionList
            listId={listId}
            items={suggestion.items}
            activeIndex={suggestion.active}
            query={suggestion.query}
            onInsert={suggestions.insertSuggestion}
          />
        </IPCallout>
      )}
    </IPStack>
  );
};

export default ExpressionEditor;
